/**
 * One owner for the two questions every cross-document link has to answer:
 * "what id does this reference point at" and "what should the link be called".
 *
 * Every document in the RFQ -> quotation -> confirmed order -> vendor PO ->
 * invoice chain stores its neighbours as ObjectId references, but what actually
 * reaches the client differs by endpoint, and all three shapes are live today:
 *
 *   raw id          confirmedOrderApi   `quotationId: string | null`
 *   populated doc   vendorPurchaseOrderApi
 *                   `rfqId: { _id, rfqId, customerName, vesselName }`
 *   id + label      clientInvoiceApi    `quotationId` alongside `quotationNumber`
 *
 * So anything that wants to LINK to a neighbour has to normalise first, and the
 * normalisation must not be re-spelled at each call site. Duplicated helpers of
 * exactly this kind are this codebase's most expensive historical defect class
 * (see the `num()` note in CLAUDE.md), so this is the owner and callers import
 * it rather than inlining `typeof x === 'string' ? x : x._id`.
 *
 * Pure and I/O-free. NOT one of the mirrored client/server utility pairs in
 * `mirroredUtilParity.test.js` — there is no server twin, because the server
 * never has to resolve a populated Mongoose ref back into a URL.
 */

/**
 * A reference as it may arrive from any of the chain's API modules.
 *
 * The object arm is `object` rather than `Record<string, unknown>` on purpose:
 * a TypeScript interface does not carry an implicit index signature, so the
 * narrower type rejects every named DTO in this codebase — `ConfirmedOrderRef`,
 * `VendorPurchaseOrderDto` and the rest would all have to be cast at each call
 * site, which is exactly the friction that gets a helper bypassed and
 * re-implemented locally. Narrowing happens inside, once.
 */
export type ChainRef = string | number | object | null | undefined;

/**
 * Where each document in the chain opens: the one owner of these paths, read by the ChainRail on a
 * document's own page and by the Home lifecycle trail (features/rfqLifecycle), so the two can never
 * send the same document to different places.
 *
 * `vendorInvoice` opens the Vendor Invoices list with that invoice open (App.tsx, `/vendor-invoices/:id?`).
 * ChainRail does not use it yet and still renders that hop inert; the trail does.
 */
export const CHAIN_PATH = {
  rfq: (id: string) => `/rfq?selected=${encodeURIComponent(id)}`,
  quotation: (id: string) => `/quotations/${id}`,
  confirmedOrder: (id: string) => `/confirmed-orders/${id}`,
  purchaseOrder: (id: string) => `/vendor-purchase-orders/${id}`,
  vendorInvoice: (id: string) => `/vendor-invoices/${id}`,
  clientInvoice: (id: string) => `/client-invoices/${id}`,
  creditNote: (id: string) => `/credit-notes/${id}`,
} as const;

/**
 * Values that are technically strings but mean "absent". These are not
 * hypothetical: an ObjectId that is null on the server becomes the four
 * characters `null` the moment something interpolates it into a template
 * literal before it reaches us, and a link built from that 404s instead of
 * being correctly hidden.
 */
const NON_IDS = new Set(['', 'null', 'undefined', 'nan', '[object object]']);

function cleanId(value: unknown): string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (NON_IDS.has(trimmed.toLowerCase())) return null;
  return trimmed;
}

/**
 * The id a chain reference points at, or null when there is nothing to link to.
 *
 * Returning null rather than an empty string is deliberate and matches
 * `utils/num.js`'s contract on the server: missing is not the same as blank,
 * and a caller must be forced to decide what to render instead of silently
 * producing `/quotations/`.
 */
export function refId(ref: ChainRef): string | null {
  if (ref === null || ref === undefined) return null;

  if (typeof ref === 'string' || typeof ref === 'number') {
    return cleanId(ref);
  }

  // An array is never a single reference. Guarded explicitly because
  // `typeof [] === 'object'`, and falling through would read `_id` off an
  // array and return null by accident rather than by decision.
  if (Array.isArray(ref)) return null;

  if (typeof ref !== 'object') return null;

  const record = ref as Record<string, unknown>;

  // A populated Mongoose document serialises `_id` to a string over JSON, but a
  // raw ObjectId can still reach us when a value is passed between components
  // without a round trip. Both answer to String().
  const raw = record._id ?? record.id;
  if (raw === null || raw === undefined) return null;

  if (typeof raw === 'object') {
    const asString = String(raw);
    return cleanId(asString);
  }

  return cleanId(raw);
}

/**
 * The first usable human-readable label on a populated reference, checked in
 * the caller's order of preference.
 *
 * A bare id string yields null: an ObjectId is not a name, and showing one in a
 * chip tells the reader nothing. Callers pass the number they already hold
 * (`quotationNumber`, `rfqNumber`) as the fallback instead.
 */
export function refLabel(ref: ChainRef, keys: readonly string[]): string | null {
  if (!ref || typeof ref !== 'object' || Array.isArray(ref)) return null;

  const record = ref as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}

/**
 * Normalise a list of references into unique, linkable entries, preserving the
 * order they arrived in.
 *
 * Deduplicates by id because a client invoice can carry the same `poId` on
 * several lines — `poMatches` is per line, so an order split across four lines
 * of one purchase order would otherwise render that purchase order four times.
 */
export function refEntries(
  refs: ChainRef[] | null | undefined,
  labelKeys: readonly string[]
): Array<{ id: string; label: string | null }> {
  if (!Array.isArray(refs)) return [];

  const seen = new Set<string>();
  const out: Array<{ id: string; label: string | null }> = [];

  for (const ref of refs) {
    const id = refId(ref);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label: refLabel(ref, labelKeys) });
  }

  return out;
}
